import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { api } from '../api/client';
import type { MediaLocation } from '../api/types';
import { useApi } from '../api/useApi';
import AsyncSection from '../components/AsyncSection';
import AddLibraryDialog from '../components/libraries/AddLibraryDialog';
import { optionName, sourceName } from '../components/libraries/librarySources';
import PageHeader from '../components/PageHeader';

export default function LibrariesPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const libraries = useApi(['libraries'], api.libraries);
  const providers = useApi(['libraries', 'providers'], api.mediaSourceProviders);
  const [adding, setAdding] = useState(false);
  const [scanFailure, setScanFailure] = useState<string>();

  const providerOf = (providerId: string) =>
    providers.data?.find((candidate) => candidate.providerId === providerId);

  const providerName = (providerId: string) => {
    const provider = providerOf(providerId);

    return provider ? sourceName(t, provider) : providerId;
  };

  /// A folder is its path; an account's list is named the way it was picked. One the source no
  /// longer offers - an account that was removed - says so instead of showing an address.
  const locationName = (location: MediaLocation) => {
    const provider = providerOf(location.providerId);

    if (!provider || provider.typedAddresses) {
      return { text: location.address, missing: false };
    }

    const option = provider.options.find((candidate) => candidate.address === location.address);

    return option
      ? { text: optionName(t, provider, option), missing: false }
      : { text: t('libraries.source.unavailable'), missing: true };
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        title={t('libraries.title')}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            disabled={!providers.data}
            onClick={() => setAdding(true)}
          >
            {t('libraries.add')}
          </Button>
        }
      />

      {scanFailure && (
        <Alert severity="warning" onClose={() => setScanFailure(undefined)}>
          {t('libraries.add.scanFailed', { detail: scanFailure })}
        </Alert>
      )}

      <AsyncSection loading={libraries.loading} error={libraries.error} onRetry={libraries.reload}>
        {libraries.data?.length ? (
          <Stack spacing={2}>
            {libraries.data.map((library) => (
              <Card key={library.id} variant="outlined">
                <CardContent>
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                      {library.name}
                    </Typography>
                    <Chip size="small" label={t(`libraryKind.${library.kind}`)} />
                    <Button
                      size="small"
                      onClick={() => {
                        void api.scanLibrary(library.id).then(libraries.reload);
                      }}
                    >
                      {t('libraries.scan')}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        void api.refreshLibraryMetadata(library.id);
                      }}
                    >
                      {t('libraries.refreshMetadata')}
                    </Button>
                    <Button size="small" onClick={() => navigate(`/libraries/${library.id}`)}>
                      {t('libraries.settings')}
                    </Button>
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('libraries.lastScan')}:{' '}
                    {library.lastScannedAt
                      ? new Date(library.lastScannedAt).toLocaleString(i18n.language)
                      : t('libraries.never')}
                  </Typography>

                  <Typography variant="overline" sx={{ display: 'block', mt: 2 }}>
                    {t('libraries.sourceNamed', { name: providerName(library.locations[0]?.providerId ?? '') })}
                  </Typography>
                  {library.locations.map((location) => {
                    const { text, missing } = locationName(location);

                    return (
                      <Typography
                        key={location.id}
                        variant="body2"
                        color={missing ? 'warning.main' : undefined}
                        sx={{ overflowWrap: 'anywhere' }}
                      >
                        {text}
                      </Typography>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <Typography color="text.secondary">{t('libraries.emptyHint')}</Typography>
        )}
      </AsyncSection>

      <AddLibraryDialog
        open={adding}
        providers={providers.data ?? []}
        onClose={() => setAdding(false)}
        onCreated={(failure) => {
          setAdding(false);
          setScanFailure(failure);
          libraries.reload();
        }}
      />
    </Stack>
  );
}
